import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Translate, withLocalize } from 'react-localize-redux';
import { PageHeader, PageWrapper, GeneralPopup, PopupTypes } from '../components/partials';
import Spinner from '../components/widgets/Spinner';
import Button from '../components/atoms/Button';
import TextField from '../components/molecules/TextField';
import { setMfaActivated } from '../actions/user';
import { getUserData } from '../selectors/user';
import { trackMixpanelEvent } from '../utils/general';
import toaster from '../utils/toaster';
import { activateMFA, deactivateMFA, getMFAMetadata } from './service';
import css from './page.css';

const TRANSLATION_KEY = 'STATIC.PAGES.TWO_FACTOR_AUTH';
const CONFIG_KEY = 'STATIC.PAGES.TWO_FACTOR_AUTH_CONFIG';
const MIXPANEL_EVENT_PREFIX = '2Factor Authentication';

const STEP_1_IMAGE = '/static/dashboard/img/icons/2fa_app_step1.png';
const STEP_3_IMAGE = '/static/dashboard/img/icons/2fa_app_step3.png';

const GENERAL_ERROR = Symbol('generalError');

const isNonEmptyString = value => typeof value === 'string' && !!value;

const isValidMetadata = value => value && isNonEmptyString(value.secret) && isNonEmptyString(value.secret_image);

const SetupStep = ({ image, stepKey, children = null }) => (
    <div className={css.step}>
        <div className={css.stepImage}>
            <img className={css.stepImg} src={image} alt="" />
        </div>
        <div className={css.stepContent}>
            <h4 className={css.stepTitle}>
                <Translate id={`${CONFIG_KEY}.${stepKey}.TITLE`} />
            </h4>
            <div className={css.stepSubtitle}>
                <Translate id={`${CONFIG_KEY}.${stepKey}.DESCRIPTION`} />
            </div>
            <p className={css.stepInstructions}>
                <Translate id={`${CONFIG_KEY}.${stepKey}.INSTRUCTIONS`} />
            </p>
            {children}
        </div>
    </div>
);

SetupStep.propTypes = {
    image: PropTypes.string.isRequired,
    stepKey: PropTypes.string.isRequired,
    children: PropTypes.node,
};

const TwoFactorAuth = ({ translate }) => {
    const dispatch = useDispatch();
    const userData = useSelector(getUserData);
    const userLoaded = !!userData.user_id;
    const mfaActivated = !!userData.mfa_activated;

    const [metadata, setMetadata] = useState(null);
    const [metadataError, setMetadataError] = useState(null);
    const [loadingMetadata, setLoadingMetadata] = useState(false);
    const [showManualCode, setShowManualCode] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [activationError, setActivationError] = useState('');
    const [activating, setActivating] = useState(false);
    const [showDeactivatePopup, setShowDeactivatePopup] = useState(false);
    const [deactivating, setDeactivating] = useState(false);

    useEffect(() => {
        if (!userLoaded || mfaActivated) return undefined;

        let cancelled = false;
        setLoadingMetadata(true);
        setMetadata(null);
        setMetadataError(null);
        setVerificationCode('');
        setActivationError('');
        setShowManualCode(false);

        getMFAMetadata()
            .then(response => {
                if (cancelled) return;
                if (response.status === 0) {
                    if (isValidMetadata(response.value)) {
                        setMetadata(response.value);
                    } else {
                        setMetadataError(GENERAL_ERROR);
                    }
                } else {
                    setMetadataError(isNonEmptyString(response.value) ? response.value : GENERAL_ERROR);
                }
            })
            .catch(() => {
                if (!cancelled) setMetadataError(GENERAL_ERROR);
            })
            .finally(() => {
                if (!cancelled) setLoadingMetadata(false);
            });

        return () => {
            cancelled = true;
        };
    }, [userLoaded, mfaActivated]);

    const handleActivate = () => {
        trackMixpanelEvent(MIXPANEL_EVENT_PREFIX, 'Activate');
        setActivationError('');
        setActivating(true);
        activateMFA(verificationCode)
            .then(response => {
                if (response.status === 0) {
                    dispatch(setMfaActivated(true));
                } else {
                    setActivationError(
                        isNonEmptyString(response.value)
                            ? response.value
                            : translate(`${TRANSLATION_KEY}.GENERAL_ERROR`)
                    );
                }
            })
            .catch(() => setActivationError(translate(`${TRANSLATION_KEY}.GENERAL_ERROR`)))
            .finally(() => setActivating(false));
    };

    const handleDeactivateClick = () => {
        trackMixpanelEvent(MIXPANEL_EVENT_PREFIX, 'Deactivate');
        setShowDeactivatePopup(true);
    };

    const handleDeactivateConfirmed = () => {
        setDeactivating(true);
        deactivateMFA()
            .then(response => {
                if (response.status === 0) {
                    dispatch(setMfaActivated(false));
                } else {
                    toaster.error(translate(`${TRANSLATION_KEY}.GENERAL_ERROR`));
                }
            })
            .catch(() => toaster.error(translate(`${TRANSLATION_KEY}.GENERAL_ERROR`)))
            .finally(() => {
                setDeactivating(false);
                setShowDeactivatePopup(false);
            });
    };

    const renderSetupSteps = () => {
        if (loadingMetadata) return <Spinner show expanded />;
        if (metadataError) {
            return (
                <h4>
                    {metadataError === GENERAL_ERROR ? (
                        <Translate id={`${TRANSLATION_KEY}.GENERAL_ERROR`} />
                    ) : (
                        metadataError
                    )}
                </h4>
            );
        }
        if (!metadata) return null;

        return (
            <>
                <p>
                    <Translate id={`${CONFIG_KEY}.PREAMBLE`} />
                </p>
                <div className={css.stepsContainer}>
                    <SetupStep image={STEP_1_IMAGE} stepKey="STEP_1" />
                    <hr className={css.stepSeparator} />
                    <SetupStep image={`data:image/jpg;base64,${metadata.secret_image}`} stepKey="STEP_2">
                        <p>
                            (
                            <button
                                type="button"
                                className={css.linkButton}
                                onClick={() => setShowManualCode(current => !current)}
                            >
                                <Translate id={`${CONFIG_KEY}.STEP_2.CANT_SCAN`} />
                            </button>
                            )
                        </p>
                        {showManualCode && (
                            <div>
                                <Translate id={`${CONFIG_KEY}.STEP_2.HELP`} data={{ code: metadata.secret }} />
                            </div>
                        )}
                    </SetupStep>
                    <hr className={css.stepSeparator} />
                    <SetupStep image={STEP_3_IMAGE} stepKey="STEP_3">
                        <div className={css.verificationForm}>
                            <TextField
                                containerClass={css.verificationCodeField}
                                placeholder="STATIC.PLACEHOLDERS.ENTER_2FA_CODE"
                                value={verificationCode}
                                onChange={setVerificationCode}
                            />
                            <Button
                                text="STATIC.BUTTONS.SETUP_TWO_FACTOR_AUTH"
                                onClick={handleActivate}
                                showSpinner={activating}
                            />
                        </div>
                        {activationError && <div className={css.verificationError}>{activationError}</div>}
                    </SetupStep>
                </div>
            </>
        );
    };

    const renderInactive = () => (
        <>
            <p>
                <Translate id={`${TRANSLATION_KEY}.P1`} />
            </p>
            <p>
                <Translate id={`${TRANSLATION_KEY}.P2`} />
            </p>
            {renderSetupSteps()}
            <div className={css.note}>
                <Translate id={`${TRANSLATION_KEY}.P3`} />
            </div>
        </>
    );

    const renderActive = () => (
        <>
            <p>
                <Translate id={`${TRANSLATION_KEY}.P1_ACTIVE`} />
            </p>
            <Button type="secondary" text="STATIC.BUTTONS.DEACTIVATE_TWO_FACTOR_AUTH" onClick={handleDeactivateClick} />
            <GeneralPopup
                open={showDeactivatePopup}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_KEY}.DEACTIVATE_MODAL.TITLE`}
                acceptText={`${TRANSLATION_KEY}.DEACTIVATE_MODAL.ACCEPT`}
                rejectText={`${TRANSLATION_KEY}.DEACTIVATE_MODAL.REJECT`}
                onAccept={handleDeactivateConfirmed}
                onReject={() => setShowDeactivatePopup(false)}
                showSpinner={deactivating}
            />
        </>
    );

    return (
        <PageWrapper>
            <PageHeader text="STATIC.PAGE_HEADERS.TWO_FACTOR_AUTH" />
            <div className={css.content}>
                {!userLoaded && <Spinner show expanded />}
                {userLoaded && (mfaActivated ? renderActive() : renderInactive())}
            </div>
        </PageWrapper>
    );
};

TwoFactorAuth.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(TwoFactorAuth);
